/**
 * Item cross-reference matcher.
 *
 * CANONICAL COPY lives at supabase/functions/_shared/itemCrossReference.mjs;
 * this web module is its mirror, kept in lockstep so the client and the Deno
 * edge pipeline decide "same product, different vendor and/or package size"
 * with the exact same deterministic rules. If you change the matching
 * behavior, change BOTH files.
 *
 * Purpose: decide whether an incoming line (from an invoice, an order guide,
 * or manual entry) is the same PRODUCT as an existing inventory item even
 * though the vendor and/or package size differ — so the line can be attached
 * to that item as another purchase option instead of creating a duplicate
 * item. Pure, in-memory, no AI, no I/O, and conservative by construction.
 *
 * What identity means here:
 *   - VENDOR is never compared. A cross-reference is by definition
 *     cross-vendor ("Whole Milk" from RD vs the Sysco item).
 *   - SIZE is never compared. unit/packSize/pack_size fields are ignored, and
 *     pure size tokens inside names ('1gal', '12oz', '4/1gal', bare counts,
 *     unit words) are dropped before matching. A different package size of
 *     the same product is a cross-reference, not a new product — size must
 *     NOT influence identity.
 *   - NAME identity: normalize (lowercase, strip punctuation, collapse
 *     whitespace, canonicalize grocery abbreviations — 'whl' → 'whole',
 *     'chkn' → 'chicken' — naive singularize), then compare the remaining
 *     token sets. Exact set equality scores 1; full containment of the
 *     smaller set (when it has >= 2 tokens) is treated as strong —
 *     'whole milk' ⊆ 'whole milk vitamin d' — scoring
 *     0.9 + 0.1 * (smaller/larger); otherwise plain token-set Jaccard.
 *     Always in [0, 1].
 *
 * SPEC tokens (extracted from name + brand) gate the name score:
 *   - certification: organic, grass-fed, kosher, halal, non-gmo, fair trade
 *   - grade:         usda prime / choice / select
 *   - fat/content:   percents ('2%'), lean ratios ('80/20'), whole / skim /
 *                    nonfat / fat free / reduced fat / low fat
 *   - preparation:   diced, sliced, shredded, ground, boneless, skinless, smoked
 *   - species:       fixed vocabulary (chicken, beef, salmon, cheddar, ...)
 *   - brand:         line.brand vs item.brand; when only one side carries an
 *                    explicit brand, the other side's leading name token may
 *                    stand in as its brand ("Horizon Whole Milk" → horizon)
 *
 *   CLAIMS (certification, preparation) are directional: every claim the LINE
 *   makes must be present on the item — line 'Organic Whole Milk' vs item
 *   'Whole Milk' CONFLICTS, because attaching would relabel an organic
 *   purchase as the conventional item. The reverse is NOT a conflict: an
 *   underspecified line ('Whole Milk') may still match a more-specified item
 *   ('Organic Whole Milk'). STATES (fat, grade, species, brand) are
 *   symmetric: compared only when BOTH sides carry a value from the family —
 *   any difference conflicts; one side missing the spec is NOT a conflict.
 *
 * Decision:
 *   score >= attachThreshold (0.90) and no spec conflicts → 'attach'
 *     …unless the matched item is strict_specification, which downgrades to
 *     'suggest' (reason 'strict_specification') so a human confirms.
 *   score >= suggestThreshold (0.75) → 'suggest'
 *   a name match at suggest level that was rejected on spec grounds → 'none'
 *     with reason 'spec_conflict:<detail>' and the conflicting item attached
 *   otherwise → 'none' ('below_threshold' / 'empty_catalog' / 'empty_name')
 *   Ties for the best clean score → 'suggest' with reason 'ambiguous' (never
 *   auto-attach when two items are indistinguishable).
 *
 * findCrossReference(catalog, line, opts?) →
 *   { decision: 'attach' | 'suggest' | 'none',
 *     itemId, itemDescription, score, reason, specConflicts: [] }
 *
 * findCrossReferenceCandidates(catalog, line, opts?) → up to 3 best
 * candidates, descending score (catalog order among ties):
 *   [{ itemId, itemDescription, score, reason, specConflicts: [],
 *      decision: 'attach' | 'suggest' }]
 * Only clean (spec-conflict-free) items at or above suggestThreshold are
 * listed — a conflicted item is excluded entirely, never presented as a
 * match option. Each entry's decision applies the single-item rules above:
 * attach-level score ⇒ 'attach', downgraded to 'suggest' (reason
 * 'strict_specification') for strict_specification items; 'suggest'
 * otherwise. findCrossReference returns the first candidate, or a 'none'
 * verdict when nothing qualifies.
 *
 *   catalog: [{ id, description, brand, unit, pack_size, vendor,
 *               strict_specification }]
 *   line:    { name, vendor, unit, packSize, brand }
 *   opts:    { attachThreshold = 0.90, suggestThreshold = 0.75 }
 */

// ── Name identity ─────────────────────────────────────────────────────────
function normalizeIdentity(value) {
  return String(value ?? '')
    .toLowerCase()
    // Normalize smart quotes / dashes / non-breaking spaces to plain ASCII.
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, ' ')
    .replace(/\u00A0/g, ' ')
    // Strip punctuation — including '%' and '/', so fat specs like '2%' and
    // '80/20' collapse to bare numbers here and are dropped as size tokens
    // below (they still participate via the spec pass, never via identity).
    .replace(/[.,/\\_()%*#&+'"`~!?:;-]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// Naive singularize: drop a trailing 's' unless the word ends in ss/us/is,
// and fold '-ies' plurals to '-y' ('patties' → 'patty', 'fries' → 'fry').
// Both sides get the same treatment, so 'hearts' vs 'heart' still agree.
function singularize(token) {
  if (token.length > 4 && token.endsWith('ies')) {
    return `${token.slice(0, -3)}y`;
  }
  if (token.length > 3 && token.endsWith('s') && !/(ss|us|is)$/.test(token)) {
    return token.slice(0, -1);
  }
  return token;
}

// Curated grocery abbreviations, canonicalized on BOTH sides during
// tokenization so vendor shorthand and plain names land on the same identity
// tokens ('Milk Whl' ≡ 'Whole Milk', 'CHKN BRST BNLS' ≡ 'Chicken Breast
// Boneless') — and on the same spec tokens ('Org Whl Milk' still carries the
// 'organic' claim, so a spec conflict is NOT rescued). Unambiguous product
// words only; ambiguous shorthands are deliberately left out. Unit/pack
// abbreviations (cs, pk, ct, gal, dz, ...) need no entry here — they are
// stripped as size tokens below; 'bx'/'bt' map to their (stripped) unit
// words so they vanish from identity the same way.
const ABBREVIATIONS = new Map([
  ['whl', 'whole'],
  ['mlk', 'milk'],
  ['brst', 'breast'],
  ['bnls', 'boneless'],
  ['chkn', 'chicken'],
  ['bf', 'beef'],
  ['prk', 'pork'],
  ['grnd', 'ground'],
  ['chs', 'cheese'],
  ['org', 'organic'],
  ['veg', 'vegetable'],
  ['frzn', 'frozen'],
  ['bx', 'box'],
  ['bt', 'bottle'],
]);

function canonicalizeToken(token) {
  return ABBREVIATIONS.get(token) ?? token;
}

// Pure size tokens must not influence identity: a number glued to a unit
// ('1gal', '12oz'), a bare number ('1', '4' from '4/1gal' once punctuation is
// stripped), or a standalone unit/pack word ('gal', 'oz', 'ct', 'pack',
// 'case', 'bottle', 'dozen', ...). Pack words stay available to pack/size
// parsing elsewhere — this only removes them from name identity scoring.
const SIZE_GLUED_RE = /^\d+(?:\.\d+)?(?:floz|oz|ml|cl|l|lbs?|g|kg|gal|qt|pt|ct|pk|pc|ea)$/;
const SIZE_BARE_RE = /^\d+(?:\.\d+)?$/;
const UOM_WORDS = new Set([
  'oz', 'floz', 'ml', 'cl', 'l', 'liter', 'liters', 'litre', 'litres',
  'lb', 'lbs', 'pound', 'pounds', 'g', 'gram', 'grams', 'kg',
  'gal', 'gallon', 'gallons', 'qt', 'quart', 'quarts', 'pt', 'pint', 'pints',
  'ct', 'count', 'pk', 'pack', 'pc', 'piece', 'pieces', 'ea',
  'case', 'cases', 'cs', 'bottle', 'bottles', 'can', 'cans', 'jar', 'jars',
  // 'boxe' is what naive singularize makes of 'boxes' — listed so the
  // plural is stripped too.
  'box', 'boxes', 'boxe', 'bag', 'bags', 'each', 'dozen', 'dozens', 'dz',
]);

function isSizeToken(token) {
  return SIZE_GLUED_RE.test(token) || SIZE_BARE_RE.test(token) || UOM_WORDS.has(token);
}

/** Normalized, abbreviation-canonicalized, singularized, size-free identity
 * tokens for a name/brand string. Abbreviations expand BEFORE singularize so
 * a shorthand ending in 's' ('bnls', 'chs') is never mangled by the
 * trailing-s rule. */
function identityTokens(value) {
  const n = normalizeIdentity(value);
  if (!n) return [];
  return n
    .split(' ')
    .map(canonicalizeToken)
    .map(singularize)
    .filter((t) => t.length > 0 && !isSizeToken(t));
}

// ── Spec extraction ───────────────────────────────────────────────────────
// Like normalizeIdentity but keeps '%' and '/' so fat specs ('2%', '80/20')
// survive; everything else non-alphanumeric becomes a space, which also turns
// 'grass-fed' / 'non-gmo' into matchable phrases ('grass fed', 'non gmo').
// Tokens are abbreviation-canonicalized too, so a shorthand claim ('Org Whl
// Milk') carries the same spec meaning as the spelled-out name and a spec
// conflict is never rescued by abbreviation.
function normalizeSpecText(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/[^a-z0-9%/\s]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(canonicalizeToken)
    .join(' ');
}

const CERTIFICATION_PATTERNS = [
  [/\borganic\b/, 'organic'],
  [/\bgrass fed\b/, 'grass-fed'],
  [/\bkosher\b/, 'kosher'],
  [/\bhalal\b/, 'halal'],
  [/\bnon gmo\b/, 'non-gmo'],
  [/\bfair trade\b/, 'fair-trade'],
];

const GRADE_RE = /\b(prime|choice|select)\b/;
const FAT_PERCENT_RE = /\b(\d+(?:\.\d+)?)\s*%/;
// Lean/fat ratios are always two digits per side — requiring \d{2} keeps pack
// sizes like '4/1gal' from being misread as a content ratio.
const FAT_RATIO_RE = /\b(\d{2})\s*\/\s*(\d{2})\b/;
const FAT_WORD_RE = /\b(whole|skim|nonfat)\b/;

const PREPARATION_WORDS = [
  'diced', 'sliced', 'shredded', 'ground', 'boneless', 'skinless', 'smoked',
];

// Fixed species/variety vocabulary — deliberately not exhaustive. Words not
// on the list simply don't participate, so an unknown word can never cause a
// false conflict (conservative by construction).
const SPECIES_WORDS = [
  'chicken', 'beef', 'pork', 'turkey', 'salmon', 'tuna', 'cod', 'tilapia',
  'shrimp', 'lamb', 'veal', 'duck', 'crab', 'lobster', 'bacon', 'ham', 'sausage',
  'romaine', 'iceberg', 'cheddar', 'mozzarella', 'parmesan', 'provolone',
  'swiss', 'feta', 'russet',
];

const PREPARATION_PATTERNS = PREPARATION_WORDS.map((w) => new RegExp(`\\b${w}\\b`));
const SPECIES_PATTERNS = SPECIES_WORDS.map((w) => new RegExp(`\\b${w}\\b`));

// Every word that carries spec meaning — used to keep a spec word ('whole',
// 'organic', 'diced', ...) from being mistaken for a brand-like leading token.
const SPEC_VOCAB = new Set([
  'organic', 'grass', 'fed', 'kosher', 'halal', 'non', 'gmo', 'fair', 'trade',
  'prime', 'choice', 'select',
  'whole', 'skim', 'nonfat', 'fat', 'free', 'reduced', 'low',
  ...PREPARATION_WORDS,
  ...SPECIES_WORDS,
]);

/**
 * Extract spec tokens from a side's name + brand. Claims (certifications,
 * preparation) are multi-value sets; states (fat, grade) reduce to a single
 * canonical value per side (a quantitative spec beats a qualitative one:
 * 'Whole Milk 2%' is '2%' milk, not whole).
 */
function extractSpecs(nameText, brandText) {
  const text = `${normalizeSpecText(nameText)} ${normalizeSpecText(brandText)}`.trim();

  const certifications = new Set();
  CERTIFICATION_PATTERNS.forEach(([re, canon]) => {
    if (re.test(text)) certifications.add(canon);
  });

  const gradeMatch = text.match(GRADE_RE);
  const grade = gradeMatch ? gradeMatch[1] : null;

  let fat = null;
  let m = text.match(FAT_PERCENT_RE);
  if (m) {
    fat = `${parseFloat(m[1])}%`;
  } else if ((m = text.match(FAT_RATIO_RE))) {
    fat = `${parseInt(m[1], 10)}/${parseInt(m[2], 10)}`;
  } else if (/\bfat free\b/.test(text)) {
    fat = 'fat-free';
  } else if (/\breduced fat\b/.test(text)) {
    fat = 'reduced-fat';
  } else if (/\blow fat\b/.test(text)) {
    fat = 'low-fat';
  } else {
    m = text.match(FAT_WORD_RE);
    if (m) fat = m[1];
  }

  const preparation = new Set();
  PREPARATION_PATTERNS.forEach((re, i) => {
    if (re.test(text)) preparation.add(PREPARATION_WORDS[i]);
  });

  const species = new Set();
  SPECIES_PATTERNS.forEach((re, i) => {
    if (re.test(text)) species.add(SPECIES_WORDS[i]);
  });

  return { certifications, fat, grade, preparation, species };
}

// ── Brand ─────────────────────────────────────────────────────────────────
// Explicit brand fields win. When only ONE side carries an explicit brand,
// the other side's leading name token may stand in as its brand ('Horizon
// Whole Milk' → horizon) — unless that token is a spec word ('Whole Milk' →
// 'whole' is a fat spec, not a brand) or already appears in the other side's
// name (then it's a shared descriptor, not a brand). Two name-derived
// candidates are never compared against each other (too guessy).
function resolveBrandTokens(brandField, ownNameTokens, otherNameTokens, otherHasExplicitBrand) {
  const fromField = new Set(identityTokens(brandField));
  if (fromField.size) return fromField;
  if (!otherHasExplicitBrand) return null;
  const candidate = ownNameTokens[0];
  if (!candidate || SPEC_VOCAB.has(candidate)) return null;
  if (otherNameTokens.includes(candidate)) return null;
  return new Set([candidate]);
}

// ── Scoring ───────────────────────────────────────────────────────────────
// Exact token-set equality → 1. Full containment of the smaller set (with >=
// 2 tokens, so a lone shared word like 'milk' can't inflate) is treated as
// strong: 0.9 + 0.1 * (smaller/larger) — e.g. 'whole milk' ⊆ 'whole milk
// vitamin d' scores 0.95. Otherwise token-set Jaccard. Always in [0, 1].
function nameScore(aTokens, bTokens) {
  const a = new Set(aTokens);
  const b = new Set(bTokens);
  if (!a.size || !b.size) return { score: 0, base: 'none' };

  let inter = 0;
  a.forEach((t) => { if (b.has(t)) inter += 1; });

  if (inter === a.size && inter === b.size) {
    return { score: 1, base: 'exact_name' };
  }

  const union = a.size + b.size - inter;
  const jaccard = union ? inter / union : 0;
  const smaller = Math.min(a.size, b.size);
  if (smaller >= 2 && inter === smaller) {
    // A TWO-token name fully contained in a longer one is suggestive, never
    // conclusive: 'Whole Milk' ⊆ 'Whole-Milk Low-Moisture Mozzarella' is a
    // different product wearing the shorter name as an adjective. Capping
    // the score just below the attach threshold keeps these as review
    // suggestions; three shared tokens and up ('Prepared Wasabi Paste' ⊆
    // 'Hon Wasabi-Style Prepared Paste') remain attach-eligible.
    const containment = 0.9 + 0.1 * (smaller / Math.max(a.size, b.size));
    return {
      score: smaller === 2 ? Math.max(jaccard, Math.min(containment, 0.89)) : Math.max(jaccard, containment),
      base: 'name_containment',
    };
  }
  return { score: jaccard, base: 'name_similarity' };
}

// ── Spec conflicts ────────────────────────────────────────────────────────
// CLAIMS (certification, preparation) are directional: the line's claims must
// all be present on the item. STATES (fat, grade, species, brand) are
// symmetric: conflict only when both sides carry the family and disagree —
// one side missing the spec is NOT a conflict.
function specConflictsFor(lineCtx, itemCtx) {
  const conflicts = [];

  lineCtx.specs.certifications.forEach((cert) => {
    if (!itemCtx.specs.certifications.has(cert)) conflicts.push(`certification:${cert}`);
  });

  if (lineCtx.specs.fat && itemCtx.specs.fat && lineCtx.specs.fat !== itemCtx.specs.fat) {
    conflicts.push(`fat:${lineCtx.specs.fat}!=${itemCtx.specs.fat}`);
  }

  if (lineCtx.specs.grade && itemCtx.specs.grade && lineCtx.specs.grade !== itemCtx.specs.grade) {
    conflicts.push(`grade:${lineCtx.specs.grade}!=${itemCtx.specs.grade}`);
  }

  lineCtx.specs.preparation.forEach((prep) => {
    if (!itemCtx.specs.preparation.has(prep)) conflicts.push(`preparation:${prep}`);
  });

  if (lineCtx.specs.species.size && itemCtx.specs.species.size) {
    let shared = 0;
    lineCtx.specs.species.forEach((s) => { if (itemCtx.specs.species.has(s)) shared += 1; });
    if (shared === 0) {
      const a = [...lineCtx.specs.species].sort().join('+');
      const b = [...itemCtx.specs.species].sort().join('+');
      conflicts.push(`species:${a}!=${b}`);
    }
  }

  if (lineCtx.brandTokens && itemCtx.brandTokens) {
    let shared = 0;
    lineCtx.brandTokens.forEach((t) => { if (itemCtx.brandTokens.has(t)) shared += 1; });
    if (shared === 0) {
      const a = [...lineCtx.brandTokens].join(' ');
      const b = [...itemCtx.brandTokens].join(' ');
      conflicts.push(`brand:${a}!=${b}`);
    }
  }

  return conflicts;
}

// ── Decision ──────────────────────────────────────────────────────────────
const DEFAULT_ATTACH_THRESHOLD = 0.9;
const DEFAULT_SUGGEST_THRESHOLD = 0.75;
const TIE_EPSILON = 1e-9;
const MAX_CANDIDATES = 3;

/**
 * Shared deterministic scan behind findCrossReference and
 * findCrossReferenceCandidates. Returns:
 *   emptyReason:   'empty_catalog' | 'empty_name' — or null when the scan ran
 *   clean:         every conflict-free item scoring > 0, descending score
 *                  (stable: catalog order among ties)
 *   candidates:    the clean items at or above suggestThreshold, each with
 *                  its per-item decision and reason
 *   topConflicted: strongest spec-conflicted name match at suggest level —
 *                  kept only to explain a 'none' rejection
 *   belowTop:      best clean match overall (for the 'below_threshold' none)
 */
function evaluateCatalog(catalog, line, opts = {}) {
  const attachThreshold = opts.attachThreshold ?? DEFAULT_ATTACH_THRESHOLD;
  const suggestThreshold = opts.suggestThreshold ?? DEFAULT_SUGGEST_THRESHOLD;

  const empty = { emptyReason: null, clean: [], candidates: [], topConflicted: null, belowTop: null };

  const items = Array.isArray(catalog) ? catalog : [];
  if (!items.length) return { ...empty, emptyReason: 'empty_catalog' };

  const lineTokens = identityTokens(line?.name);
  if (!lineTokens.length) return { ...empty, emptyReason: 'empty_name' };

  const lineSpecs = extractSpecs(line.name, line.brand);
  const lineHasExplicitBrand = identityTokens(line.brand).length > 0;

  const clean = [];
  let topConflicted = null;

  items.forEach((item) => {
    const itemTokens = identityTokens(item?.description);
    if (!itemTokens.length) return;

    const { score, base } = nameScore(lineTokens, itemTokens);
    if (score <= 0) return;

    const itemSpecs = extractSpecs(item.description, item.brand);
    const itemHasExplicitBrand = identityTokens(item.brand).length > 0;
    const specConflicts = specConflictsFor(
      {
        specs: lineSpecs,
        brandTokens: resolveBrandTokens(line.brand, lineTokens, itemTokens, itemHasExplicitBrand),
      },
      {
        specs: itemSpecs,
        brandTokens: resolveBrandTokens(item.brand, itemTokens, lineTokens, lineHasExplicitBrand),
      },
    );

    if (specConflicts.length) {
      // A spec conflict means "not the same product" — never attach or
      // suggest it. Remember the strongest conflicted name match only to
      // explain the rejection when nothing clean qualifies.
      if (score >= suggestThreshold && (!topConflicted || score > topConflicted.score)) {
        topConflicted = { item, score, specConflicts };
      }
      return;
    }
    clean.push({ item, score, base });
  });

  // Stable sort keeps catalog order among equal scores.
  clean.sort((a, b) => b.score - a.score);

  // Per-item decision: the findCrossReference rules applied candidate by
  // candidate (a strict_specification item is never attach-eligible).
  const candidates = clean
    .filter((c) => c.score >= suggestThreshold)
    .map((c) => {
      if (c.score >= attachThreshold) {
        return c.item.strict_specification
          ? { item: c.item, score: c.score, reason: 'strict_specification', decision: 'suggest' }
          : { item: c.item, score: c.score, reason: c.base, decision: 'attach' };
      }
      return { item: c.item, score: c.score, reason: c.base, decision: 'suggest' };
    });

  return { emptyReason: null, clean, candidates, topConflicted, belowTop: clean[0] ?? null };
}

/**
 * Up to MAX_CANDIDATES best-scoring match candidates for `line`, descending
 * score (catalog order among ties). Only clean items at or above
 * suggestThreshold are listed — a spec-conflicted item is excluded entirely,
 * never presented as a match option. Each entry carries the per-item
 * decision ('attach', or 'suggest' at suggest level / for strict items).
 */
export function findCrossReferenceCandidates(catalog, line, opts = {}) {
  const { candidates } = evaluateCatalog(catalog, line, opts);
  return candidates.slice(0, MAX_CANDIDATES).map((c) => ({
    itemId: c.item.id,
    itemDescription: c.item.description,
    score: c.score,
    reason: c.reason,
    specConflicts: [],
    decision: c.decision,
  }));
}

/**
 * Decide whether `line` is the same product as an item in `catalog`, despite
 * a different vendor and/or package size. Returns the single best-scoring
 * candidate — the first entry of the ranked candidate list — or a 'none'
 * verdict. See the module header for the full rules.
 */
export function findCrossReference(catalog, line, opts = {}) {
  const result = (decision, item, score, reason, specConflicts = []) => ({
    decision,
    itemId: item?.id ?? null,
    itemDescription: item?.description ?? null,
    score,
    reason,
    specConflicts,
  });

  const { emptyReason, clean, candidates, topConflicted, belowTop } = evaluateCatalog(catalog, line, opts);
  if (emptyReason) return result('none', null, 0, emptyReason);

  const top = candidates[0];
  if (top) {
    const tied = clean.filter((c) => Math.abs(c.score - top.score) < TIE_EPSILON);
    if (tied.length > 1) {
      // Two indistinguishable items — never auto-attach a guess.
      return result('suggest', top.item, top.score, 'ambiguous');
    }
    return result(top.decision, top.item, top.score, top.reason);
  }

  if (topConflicted) {
    return result(
      'none',
      topConflicted.item,
      topConflicted.score,
      `spec_conflict:${topConflicted.specConflicts[0]}`,
      topConflicted.specConflicts,
    );
  }

  return result('none', belowTop?.item ?? null, belowTop?.score ?? 0, 'below_threshold');
}
